import { buildPropertyImageAlt } from '../../Utils/propertyImageAlt';
/* eslint-disable */
import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Button,
  Chip,
  Divider,
  Card,
  CardContent,
  TextField,
  Box,
  Typography,
  IconButton,
  Stack,
  Grid,
  CardMedia,
  Avatar,
  Container,
  CircularProgress,
  Modal,
  Alert,
} from "@mui/material";
import {
  ArrowBack as BackIcon,
  LocationOn as MapPinIcon,
  Bed as BedIcon,
  Bathtub as BathIcon,
  SquareFoot as SquareIcon,
  Favorite as HeartIcon,
  FavoriteBorder as HeartOutlineIcon,
  Share as ShareIcon,
  Phone as PhoneIcon,
  Email as MailIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as XCircleIcon,
  Business as BuildingIcon,
  DirectionsCar as CarIcon,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  ContentCopy as CopyIcon,
  Close as CloseIcon,
  Description as FileIcon,
  GetApp as DownloadIcon,
} from "@mui/icons-material";
import { convertGoogleDriveUrl, convertGoogleDriveVideoUrl, handleImageError } from "../../Constants/images";
import darpanIcon from "../../Assets/images/svg/darpan_icon.svg";
import { Header } from "../../Components/Header";
import { propertyAPI, savedPropertyAPI, propertyDraftAPI, legalDocumentAPI } from "../../services/api";
import { fetchFileById } from "../../Api/request/propertyDetail";
import UseAxios from "../../Api/http/http";
import { transformProperty } from "../../Utils/propertyTransform";
import { decryptRouteParam, encryptRouteParam } from "../../Utils/encrypt-decrypt";
import { trackEvent } from "../../Utils/analytics";
import { useAuth } from "../../Contexts/AuthContext";
import { useToast } from "../../context/ToastContext";
import useWindowResize from "../../Hooks/useWindowResize";

export function PropertyDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { userProfile, isLoggedIn, logout } = useAuth();
  const roleId = userProfile?.role_id ?? userProfile?.roleId;
  const isAdmin = Number(roleId) === 1;
  const { showWarning } = useToast();
  const isDesktopView = useWindowResize();

  // Check if viewing a draft property
  const isDraftView = location.pathname.includes('/draft/');
  const resolvedPropertyId = isDraftView ? id : (decryptRouteParam(id) || id);

  const buildCanonicalPropertyPath = () => {
    if (isDraftView) {
      return `/property/draft/${id}`;
    }

    // Always emit a URL-safe encrypted id for shared/canonical links.
    const decryptedRouteId = decryptRouteParam(id);
    const encryptedRouteId = encryptRouteParam(decryptedRouteId || resolvedPropertyId || id);

    return encryptedRouteId
      ? `/property/${encryptedRouteId}`
      : window.location.pathname;
  };

  const [property, setProperty] = useState(null);

  const [loading, setLoading] = useState(true);

  const [activeStep, setActiveStep] = useState(0);
  const [videoBlobUrls, setVideoBlobUrls] = useState({});
  const [imageBlobUrls, setImageBlobUrls] = useState({});
  const [isCarouselImageResolving, setIsCarouselImageResolving] = useState(false);
  const imageBlobUrlsRef = useRef([]);
  const imageBlobFetchPromisesRef = useRef({});
  const hasTriggeredImageBlobFallbackRef = useRef(false);
  const [documentBlobMap, setDocumentBlobMap] = useState({});
  const [isSaved, setIsSaved] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [shareUrlNonce, setShareUrlNonce] = useState("");
  const [shareMetadata, setShareMetadata] = useState(null);
  const viewedPropertyIdRef = useRef("");
  const touchStartXRef = useRef(null);

  // Inject property-specific OG meta tags so browsers / share sheets pick them up
  useEffect(() => {
    const titleFromProperty =
      property?.title || property?.propertyType || property?.property_type || "Property";
    const firstImageCandidate = Array.isArray(property?.images) ? property.images[0] : null;
    const firstImageRawUrl =
      typeof firstImageCandidate === "string"
        ? firstImageCandidate
        : firstImageCandidate?.url ||
          firstImageCandidate?.imageUrl ||
          firstImageCandidate?.src ||
          "";
    const fallbackImageUrl = firstImageRawUrl ? convertGoogleDriveUrl(firstImageRawUrl) : "";
    const ogShareTitle = shareMetadata?.title || titleFromProperty;
    const ogShareDescription =
      shareMetadata?.shortDescription || "View this property on Vruddhi Properties";
    const derivedPropertyDescription = String(
      shareMetadata?.shortDescription ||
      property?.otherDetails ||
      property?.anyOtherDetails ||
      property?.property_description ||
      "View this property on Vruddhi Properties"
    )
      .replace(/\s+/g, " ")
      .trim()
      .slice(0, 180);
    const ogShareImageUrl = shareMetadata?.shareImageUrl || fallbackImageUrl;
    const canonicalPath = buildCanonicalPropertyPath();
    const ogShareUrl = `${window.location.origin}${canonicalPath}`;

    const setMetaByProperty = (property, content) => {
      let el = document.querySelector(`meta[property="${property}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('property', property);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    const setMetaByName = (name, content) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    const setCanonicalLink = (href) => {
      let canonical = document.querySelector('link[rel="canonical"]');
      if (!canonical) {
        canonical = document.createElement('link');
        canonical.setAttribute('rel', 'canonical');
        document.head.appendChild(canonical);
      }
      canonical.setAttribute('href', href);
    };

    const prevTitle = document.title;
    document.title = `${ogShareTitle} | Vruddhi Properties`;

    setMetaByName('description', derivedPropertyDescription);
    setMetaByProperty('og:title', ogShareTitle);
    setMetaByProperty('og:description', ogShareDescription);
    setMetaByProperty('og:image', ogShareImageUrl || '');
    setMetaByProperty('og:url', ogShareUrl);
    setMetaByProperty('og:type', 'website');
    setMetaByName('twitter:card', 'summary_large_image');
    setMetaByName('twitter:title', ogShareTitle);
    setMetaByName('twitter:description', ogShareDescription);
    setMetaByName('twitter:image', ogShareImageUrl || '');
    setCanonicalLink(ogShareUrl);

    return () => {
      document.title = prevTitle;
      setMetaByName('description', 'Vruddhi Properties - Your go to platform to manage your properties needs');
      setMetaByProperty('og:title', 'Vruddhi Properties');
      setMetaByProperty('og:description', 'Your go to platform to manage your properties needs');
      setMetaByProperty('og:image', '/favicon.svg');
      setMetaByProperty('og:url', 'https://www.vruddhiproperties.com');
      setMetaByName('twitter:card', 'summary');
      setMetaByName('twitter:title', 'Vruddhi Properties');
      setMetaByName('twitter:description', 'Your go to platform to manage your properties needs');
      setMetaByName('twitter:image', '/favicon.svg');
      setCanonicalLink('https://www.vruddhiproperties.com/');
    };
  }, [id, isDraftView, property, resolvedPropertyId, shareMetadata]);

  const parseMediaList = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    if (typeof value === "string") {
      try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        return [];
      }
    }
    if (typeof value === "object") {
      if (Array.isArray(value.data)) return value.data;
      if (Array.isArray(value.items)) return value.items;
      if (Array.isArray(value.files)) return value.files;
      if (Array.isArray(value.receiptData)) return value.receiptData;
    }
    return [];
  };

  const parseDocumentGroups = (value) => {
    if (!value) return {};
    if (typeof value === "string") {
      try {
        const parsed = JSON.parse(value);
        return parsed && typeof parsed === "object" ? parsed : {};
      } catch (e) {
        return {};
      }
    }
    return typeof value === "object" ? value : {};
  };

  const extractFileIdFromMediaUrl = (value) => {
    if (!value || typeof value !== "string") return null;

    const trimmed = value.trim();
    if (!trimmed) return null;

    const queryMatch = trimmed.match(/[?&](?:id|fileId|file_id)=([a-zA-Z0-9_-]+)/i);
    if (queryMatch?.[1]) return queryMatch[1];

    const pathMatch = trimmed.match(/\/(?:images|videos)\/([a-zA-Z0-9_-]+)(?:[/?#]|$)/i);
    if (pathMatch?.[1]) return pathMatch[1];

    const driveMatch = trimmed.match(/\/d\/([a-zA-Z0-9_-]+)(?:[/?#]|$)/i);
    if (driveMatch?.[1]) return driveMatch[1];

    return null;
  };

  const getMediaFileId = (item) => {
    if (!item) return null;

    if (typeof item === "string") {
      if (!isRenderableUrl(item)) return item;
      return extractFileIdFromMediaUrl(item);
    }

    if (typeof item !== "object") return null;

    return (
      item.id ||
      item.fileId ||
      item.file_id ||
      item.video_id ||
      item.videoId ||
      item.image_id ||
      item.imageId ||
      item.media_id ||
      item.mediaId ||
      item.property_image_id ||
      item.propertyImageId ||
      item.drive_file_id ||
      item.driveFileId ||
      extractFileIdFromMediaUrl(
        item.preview || item.uploadUrl || item.gcsUrl || item.url || item.path || ""
      ) ||
      null
    );
  };

  const isGoogleDriveLikeId = (value) => {
    if (!value || typeof value !== "string") return false;
    return /^[a-zA-Z0-9_-]{20,}$/.test(value);
  };

  const buildGoogleDriveVideoUrlById = (fileId) => {
    if (!isGoogleDriveLikeId(fileId)) return null;
    return `https://drive.google.com/uc?export=view&id=${fileId}`;
  };

  const normalizeDocumentKey = (key) => {
    if (key === "original_mother_deed") return "mother_deed";
    return key;
  };

  const fetchBlobByFileName = async (filename, mimeType = "application/octet-stream") => {
    if (!filename) return null;

    const payloadCandidates = [
      { filename },
      { fileName: filename },
      { name: filename },
    ];

    for (const payload of payloadCandidates) {
      try {
        const response = await UseAxios(fetchFileById(payload));
        if (response?.data instanceof Blob) {
          return response.data;
        }
        if (response?.data instanceof ArrayBuffer) {
          return new Blob([response.data], { type: mimeType });
        }
      } catch (error) {
        // Try next payload format.
      }
    }

    return null;
  };

  const getDisplayDocuments = (docKey, fallbackDocuments) => {
    const prefetchedDocuments = documentBlobMap[docKey];
    if (Array.isArray(prefetchedDocuments) && prefetchedDocuments.length > 0) {
      return prefetchedDocuments;
    }
    return fallbackDocuments;
  };

  useEffect(() => {
    const checkSavedStatus = async () => {
      if (isLoggedIn && id) {
        try {
          const maybeNumericId = Number(id);
          if (!Number.isFinite(maybeNumericId)) {
            return;
          }
          const response = await savedPropertyAPI.getSavedPropertyIds();
          const savedIds = response.data?.data?.propertyIds || [];
          setIsSaved(savedIds.includes(parseInt(maybeNumericId, 10)));
        } catch (error) {
          console.error("Error checking saved status:", error);
        }
      }
    };
    checkSavedStatus();
  }, [isLoggedIn, id]);

  useEffect(() => {
    const checkSavedStatusByProperty = async () => {
      if (!isLoggedIn || !property?.id) return;
      try {
        const response = await savedPropertyAPI.getSavedPropertyIds();
        const savedIds = response.data?.data?.propertyIds || [];
        setIsSaved(savedIds.includes(parseInt(property.id, 10)));
      } catch (error) {
        console.error("Error checking saved status by property:", error);
      }
    };
    checkSavedStatusByProperty();
  }, [isLoggedIn, property?.id]);

  const handleToggleSave = async () => {
    if (!isLoggedIn) {
      navigate("/login");
      return;
    }

    setIsSaving(true);
    try {
      const trackedPropertyId = String(property?.id || resolvedPropertyId || id || "");
      const trackedPropertyType = String(property?.propertyType || property?.property_type || "");

      if (isSaved) {
        await savedPropertyAPI.unsaveProperty(id);
        setIsSaved(false);
        trackEvent("remove_from_wishlist", {
          item_id: trackedPropertyId,
          item_category: trackedPropertyType,
          page_path: `${location.pathname}${location.search}`,
        });
      } else {
        await savedPropertyAPI.saveProperty(id);
        setIsSaved(true);
        trackEvent("add_to_wishlist", {
          item_id: trackedPropertyId,
          item_category: trackedPropertyType,
          page_path: `${location.pathname}${location.search}`,
        });
      }
    } catch (error) {
      console.error("Error toggling save:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleShareClick = async () => {
    const nonce = shareUrlNonce || Date.now().toString(36);
    if (!shareUrlNonce) {
      setShareUrlNonce(nonce);
    }
    const shareUrl = `${window.location.origin}${canonicalPath}${nonce ? `?spv=${nonce}` : ""}`;
    const payload = {
      text: [
        shareBrandTitle,
        shareBrandTagline,
        "",
        "",
        shareUrl,
      ]
        .filter(Boolean)
        .join("\n"),
    };
    setLinkCopied(false);

    if (navigator?.share) {
      try {
        await navigator.share(payload);
        return;
      } catch (error) {
        // User can cancel share sheet; open modal as fallback.
      }
    }

    setShareModalOpen(true);
  };

  const handleCopyLink = async () => {
    if (!navigator?.clipboard?.writeText) return;
    const nonce = shareUrlNonce || Date.now().toString(36);
    if (!shareUrlNonce) {
      setShareUrlNonce(nonce);
    }
    const shareUrl = `${window.location.origin}${canonicalPath}${nonce ? `?spv=${nonce}` : ""}`;
    await navigator.clipboard.writeText(shareUrl);
    setLinkCopied(true);
  };

  const handleNativeShare = async () => {
    if (!navigator?.share) return;

    try {
      const nonce = shareUrlNonce || Date.now().toString(36);
      if (!shareUrlNonce) {
        setShareUrlNonce(nonce);
      }
      const shareUrl = `${window.location.origin}${canonicalPath}${nonce ? `?spv=${nonce}` : ""}`;
      const payload = {
        text: [
          shareBrandTitle,
          shareBrandTagline,
          "",
          "",
          shareUrl,
        ]
          .filter(Boolean)
          .join("\n"),
      };
      await navigator.share(payload);
    } catch (error) {
      // User can cancel share sheet.
    }
  };

  const handleCloseShareModal = () => {
    setShareModalOpen(false);
    setLinkCopied(false);
  };

  const handleSearch = (query) => {
    if (!query || query.trim() === "") {
      sessionStorage.removeItem("propertySearchQuery");
      sessionStorage.setItem("propertyIsSearchMode", "false");
      navigate("/");
    } else {
      const normalizedQuery = query.trim();
      sessionStorage.setItem("propertySearchQuery", normalizedQuery);
      sessionStorage.setItem("propertyIsSearchMode", "true");
      navigate("/", {
        state: {
          seoSearchQuery: normalizedQuery,
        },
      });
    }
  };

    useEffect(() => {
    const fetchProperty = async () => {
      try {
        // Always fetch fresh data from API
        setLoading(true);

        // Use draft API for draft properties, regular API for listed properties.
        // For listed properties, retry with raw route id when decrypted id fails.
        let response;
        if (isDraftView) {
          response = await propertyDraftAPI.getDraftById(id);
        } else {
          try {
            response = await propertyAPI.getPropertyById(resolvedPropertyId);
          } catch (primaryError) {
            if (String(resolvedPropertyId) !== String(id)) {
              response = await propertyAPI.getPropertyById(id);
            } else {
              throw primaryError;
            }
          }
        }
        console.log('Property API Response:', response.data);
        
        // Handle different response structures
        let propertyData = null;
        if (response.data.data?.property) {
          propertyData = response.data.data.property;
        } else if (response.data.data) {
          propertyData = response.data.data;
        } else if (response.data.property) {
          propertyData = response.data.property;
        } else if (response.data) {
          propertyData = response.data;
        }
        
        if (!propertyData) {
          console.error('Could not find property data in response');
          setProperty(null);
          setLoading(false);
          return;
        }
        
        // Ensure images is an empty array if null
        if (propertyData.images === null || propertyData.images === undefined) {
          propertyData.images = [];
        }
        
        const transformedProperty = transformProperty(propertyData);
        const propertyDocuments = parseDocumentGroups(propertyData.property_documents);
        console.log('Transformed Property:', transformedProperty);
        setProperty({
          ...transformedProperty,
          propertyDocuments,
        });
        
        // Extract share metadata from API response
        if (response.data.data?.share) {
          setShareMetadata(response.data.data.share);
        }
      } catch (error) {
        console.error("Error fetching property:", error);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchProperty();
    } else {
      // If no ID, stop loading and show "not found" state
      setLoading(false);
    }
  }, [id, isDraftView, resolvedPropertyId]);

  useEffect(() => {
    let mounted = true;

    const loadVideoBlobs = async () => {
      if (mounted) setVideoBlobUrls({});
    };

    loadVideoBlobs();

    return () => {
      mounted = false;
    };
  }, [property]);

  useEffect(() => {
    const trackedPropertyId = String(property?.id || "");
    if (!trackedPropertyId) return;
    if (viewedPropertyIdRef.current === trackedPropertyId) return;

    trackEvent("view_item", {
      item_id: trackedPropertyId,
      item_name: String(
        property?.title || property?.propertyName || property?.property_name || "Property"
      ),
      item_category: String(property?.propertyType || property?.property_type || ""),
      item_category2: String(property?.propertySubType || property?.property_sub_type || ""),
      price: Number(property?.expectedPrice || property?.expected_price || 0) || undefined,
      currency: "INR",
      page_path: `${location.pathname}${location.search}`,
    });

    viewedPropertyIdRef.current = trackedPropertyId;
  }, [
    location.pathname,
    location.search,
    property?.expectedPrice,
    property?.expected_price,
    property?.id,
    property?.propertySubType,
    property?.propertyType,
    property?.property_name,
    property?.property_sub_type,
    property?.property_type,
    property?.title,
    property?.propertyName,
  ]);

  useEffect(() => {
    let mounted = true;
    const createdObjectUrls = [];

    const loadDocumentBlobs = async () => {
      const propertyDocuments = property?.propertyDocuments;

      if (!propertyDocuments || Object.keys(propertyDocuments).length === 0) {
        if (mounted) setDocumentBlobMap({});
        return;
      }

      const nextMap = {};

      for (const [docType, docEntries] of Object.entries(propertyDocuments)) {
        const normalizedKey = normalizeDocumentKey(docType);
        const items = Array.isArray(docEntries) ? docEntries : [];

        if (!nextMap[normalizedKey]) {
          nextMap[normalizedKey] = [];
        }

        for (const fileInfo of items) {
          const fileId = fileInfo?.id || fileInfo?.fileId || fileInfo?.file_id;
          const fallbackName =
            fileInfo?.originalName ||
            fileInfo?.filename ||
            fileInfo?.name ||
            `${normalizedKey}-document`;

          if (fileId) {
            try {
              const response = await UseAxios(fetchFileById({ fileId }));
              let blob = null;

              if (response?.data instanceof Blob) {
                blob = response.data;
              } else if (response?.data instanceof ArrayBuffer) {
                blob = new Blob([response.data], {
                  type: fileInfo?.type || "application/octet-stream",
                });
              }

              if (!blob || blob.size === 0) continue;

              const objectUrl = URL.createObjectURL(blob);
              createdObjectUrls.push(objectUrl);
              nextMap[normalizedKey].push({
                ...fileInfo,
                id: fileInfo?.id || fileId,
                fileId: fileInfo?.fileId || fileId,
                file_id: fileInfo?.file_id || fileId,
                url: objectUrl,
                path: objectUrl,
                filename: fallbackName,
                originalName: fallbackName,
                name: fallbackName,
                type: blob.type || fileInfo?.type || "application/octet-stream",
                isFetchedBlob: true,
              });
              continue;
            } catch (error) {
              console.error("Failed to fetch document blob", fileId, error);
            }
          }

          if (fallbackName) {
            try {
              const blob = await fetchBlobByFileName(
                fallbackName,
                fileInfo?.type || "application/octet-stream"
              );

              if (blob && blob.size > 0) {
                const objectUrl = URL.createObjectURL(blob);
                createdObjectUrls.push(objectUrl);
                nextMap[normalizedKey].push({
                  ...fileInfo,
                  url: objectUrl,
                  path: objectUrl,
                  filename: fallbackName,
                  originalName: fallbackName,
                  name: fallbackName,
                  type: blob.type || fileInfo?.type || "application/octet-stream",
                  isFetchedBlob: true,
                });
                continue;
              }
            } catch (error) {
              console.error("Failed to fetch document blob by filename", fallbackName, error);
            }
          }

          const rawUrl = fileInfo?.url || fileInfo?.path || fileInfo?.uploadUrl || fileInfo?.gcsUrl;
          if (rawUrl) {
            nextMap[normalizedKey].push({
              ...fileInfo,
              url: rawUrl,
              path: rawUrl,
              filename: fallbackName,
              originalName: fallbackName,
              name: fallbackName,
            });
            continue;
          }

          if (fallbackName) {
            nextMap[normalizedKey].push({
              ...fileInfo,
              filename: fallbackName,
              originalName: fallbackName,
              name: fallbackName,
            });
          }
        }
      }

      if (nextMap.mother_deed && !nextMap.original_mother_deed) {
        nextMap.original_mother_deed = nextMap.mother_deed;
      }
      if (nextMap.original_mother_deed && !nextMap.mother_deed) {
        nextMap.mother_deed = nextMap.original_mother_deed;
      }

      if (mounted) {
        setDocumentBlobMap(nextMap);
      } else {
        createdObjectUrls.forEach((url) => URL.revokeObjectURL(url));
      }
    };

    loadDocumentBlobs();

    return () => {
      mounted = false;
      createdObjectUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [property?.propertyDocuments]);

  useEffect(() => {
    return () => {
      imageBlobUrlsRef.current.forEach((url) => {
        try {
          URL.revokeObjectURL(url);
        } catch (e) {
          // Ignore URL revoke failures during unmount cleanup.
        }
      });
      imageBlobUrlsRef.current = [];
      imageBlobFetchPromisesRef.current = {};
    };
  }, []);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (!property) {
    return (
      <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
        <Header
          isLoggedIn={isLoggedIn}
          isAdmin={isAdmin}
          userEmail={userProfile?.email || ""}
          onSignIn={() => navigate("/login")}
          onSignOut={() => {
            logout();
            navigate("/");
          }}
          onHomeClick={() => navigate("/")}
          onMyPropertiesClick={() => navigate("/my-properties")}
          onManageMyPropertiesClick={() => navigate("/property-list")}
          onUnderVerificationClick={() => navigate('/all-properties')}
          onUsersClick={() => navigate('/users')}
          onMyProfileClick={() => navigate("/my-profile")}
          onSavedPropertiesClick={() => navigate("/saved-properties")}
          onSearch={handleSearch}
        />
        <Container sx={{ py: 8 }}>
          <Typography variant="h4">Property not found</Typography>
          <Button
            startIcon={<BackIcon />}
            onClick={() => navigate("/")}
            sx={{ mt: 2 }}
          >
            Back to Home
          </Button>
        </Container>
      </Box>
    );
  }

  const getProp = (camelCase, snakeCase, defaultValue = "") => {
    return property[camelCase] !== undefined
      ? property[camelCase]
      : property[snakeCase] || defaultValue;
  };

  const parseArray = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    if (typeof value === "string") {
      try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        return [];
      }
    }
    if (typeof value === "object") {
      if (Array.isArray(value.data)) return value.data;
      if (Array.isArray(value.items)) return value.items;
      if (Array.isArray(value.files)) return value.files;
      if (Array.isArray(value.receiptData)) return value.receiptData;
    }
    return [];
  };

  const pickFirstNonEmptyArray = (...candidates) => {
    for (const candidate of candidates) {
      const parsed = parseArray(candidate);
      if (parsed.length > 0) return parsed;
    }
    return [];
  };

  const isRenderableUrl = (value) => {
    if (!value || typeof value !== "string") return false;
    return (
      value.startsWith("http://") ||
      value.startsWith("https://") ||
      value.startsWith("/") ||
      value.startsWith("data:") ||
      value.startsWith("blob:")
    );
  };

  const buildPropertyImageProxyUrl = (fileId) => {
    const propertyId =
      property?.property_id ||
      property?.propertyId ||
      property?.id ||
      null;

    if (!propertyId || !fileId) return null;

    return `/project-management-service/api/v1/properties/${encodeURIComponent(String(propertyId))}/images/${encodeURIComponent(String(fileId))}`;
  };

  const buildPropertyVideoProxyUrl = (fileId) => {
    const propertyId =
      property?.property_id ||
      property?.propertyId ||
      property?.id ||
      null;

    if (!propertyId || !fileId) return null;

    return `/project-management-service/api/v1/properties/${encodeURIComponent(String(propertyId))}/videos/${encodeURIComponent(String(fileId))}`;
  };

  const resolveMediaUrl = (item, mediaType = "image") => {
    if (!item) return null;
    if (typeof item === "string") {
      if (!isRenderableUrl(item)) {
        return mediaType === "image" ? buildPropertyImageProxyUrl(item) : buildPropertyVideoProxyUrl(item);
      }
      if (mediaType === "video") {
        return item;
      }

      return convertGoogleDriveUrl(item);
    }
    if (typeof item === "object") {
      const rawUrl =
        item.preview ||
        item.uploadUrl ||
        item.gcsUrl ||
        item.url ||
        item.path ||
        null;

      if (rawUrl) {
        return mediaType === "video"
          ? rawUrl
          : convertGoogleDriveUrl(rawUrl);
      }

      const fileId =
        item.id ||
        item.fileId ||
        item.file_id ||
        item.image_id ||
        item.imageId ||
        item.property_image_id ||
        item.propertyImageId ||
        item.drive_file_id ||
        item.driveFileId ||
        null;

      if (mediaType === "image") {
        return buildPropertyImageProxyUrl(fileId);
      }

      return buildPropertyVideoProxyUrl(fileId);
    }
    return null;
  };

  const getMediaId = (item) => {
    return getMediaFileId(item);
  };

  const extractGoogleDriveFileId = (url) => {
    if (!url || typeof url !== "string") return null;

    const idMatch = url.match(/[?&]id=([a-zA-Z0-9_-]+)/);
    if (idMatch?.[1]) return idMatch[1];

    const dMatch = url.match(/\/d\/([a-zA-Z0-9_-]+)/);
    if (dMatch?.[1]) return dMatch[1];

    return null;
  };

  const toGoogleDrivePreviewUrl = (url) => {
    const fileId = extractGoogleDriveFileId(url);
    return fileId ? `https://drive.google.com/file/d/${fileId}/preview` : url;
  };

  const isVideoItem = (item, sourceType = "image") => {
    if (!item) return sourceType === "video";

    if (typeof item === "object") {
      const rawType = String(item.type || item.mimeType || item.contentType || "").toLowerCase();
      if (rawType.includes("video")) return true;
      if (rawType.includes("image")) return false;

      const rawName = String(item.name || item.fileName || item.filename || "").toLowerCase();
      if (/\.(mp4|mov|webm|mkv|avi|m4v)$/i.test(rawName)) return true;
      if (/\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(rawName)) return false;
    }

    const rawUrl =
      typeof item === "string"
        ? item
        : (item.preview || item.uploadUrl || item.gcsUrl || item.url || item.path || "");
    const lowerUrl = String(rawUrl).toLowerCase();
    if (/\.(mp4|mov|webm|mkv|avi|m4v)(\?|$)/.test(lowerUrl)) return true;
    if (/\.(jpg|jpeg|png|gif|webp|bmp|svg)(\?|$)/.test(lowerUrl)) return false;

    return sourceType === "video";
  };

  const imageMedia = pickFirstNonEmptyArray(
    property.property_images,
    property.propertyImages,
    property.images
  );

  const videoMedia = pickFirstNonEmptyArray(
    property.videos,
    property.property_videos,
    property.propertyVideos
  );

  const candidateMedia = [
    ...imageMedia.map((item) => ({ item, type: "image" })),
    ...videoMedia.map((item) => ({ item, type: "video" })),
  ];

  const mediaSlides = candidateMedia
    .map(({ item, type: sourceType }) => {
      const type = isVideoItem(item, sourceType) ? "video" : "image";
      const mediaId = getMediaId(item);
      const mimeType =
        typeof item === "object"
          ? (item.type || item.mimeType || item.contentType || "")
          : "";
      const blobUrl = mediaId
        ? (type === "video" ? videoBlobUrls[mediaId] : imageBlobUrls[mediaId])
        : null;
      const url = blobUrl || resolveMediaUrl(item, type);
      return url ? { type, url, mediaId, mimeType } : null;
    })
    .filter(Boolean)
    .filter((slide, index, arr) => arr.findIndex((x) => x.type === slide.type && x.url === slide.url) === index);

  const hasMedia = mediaSlides.length > 0;

  const slides = mediaSlides;

  const maxSteps = slides.length;
  const boundedActiveStep = Math.min(Math.max(activeStep, 0), Math.max(maxSteps - 1, 0));
  const preloadImageSlides = (isDesktopView
    ? [slides[boundedActiveStep + 1]]
    : [])
    .filter((slide) => slide && slide.type === "image" && slide.url)
    .filter((slide, index, arr) => arr.findIndex((item) => item.url === slide.url) === index);

  const fetchImageBlobUrlById = async (fileId) => {
    if (!fileId) return "";

    if (imageBlobUrls[fileId]) {
      return imageBlobUrls[fileId];
    }

    if (imageBlobFetchPromisesRef.current[fileId]) {
      return imageBlobFetchPromisesRef.current[fileId];
    }

    const fetchPromise = (async () => {
      const response = await UseAxios(fetchFileById({ fileId }));
      let blob = null;

      if (response?.data instanceof Blob) {
        blob = response.data;
      } else if (response?.data instanceof ArrayBuffer) {
        blob = new Blob([response.data], { type: "image/jpeg" });
      }

      if (!blob || blob.size <= 0) {
        return "";
      }

      const objectUrl = URL.createObjectURL(blob);
      imageBlobUrlsRef.current.push(objectUrl);
      setImageBlobUrls((prev) => ({
        ...prev,
        [fileId]: objectUrl,
      }));

      return objectUrl;
    })()
      .catch(() => "")
      .finally(() => {
        delete imageBlobFetchPromisesRef.current[fileId];
      });

    imageBlobFetchPromisesRef.current[fileId] = fetchPromise;
    return fetchPromise;
  };

  const warmupNearbyImageBlobs = (baseStep = boundedActiveStep) => {
    const candidates = [
      slides[baseStep],
      slides[baseStep + 1],
      slides[baseStep + 2],
    ]
      .filter((slide) => slide && slide.type === "image" && slide.mediaId)
      .map((slide) => slide.mediaId)
      .filter((mediaId, index, arr) => arr.indexOf(mediaId) === index)
      .filter((mediaId) => !imageBlobUrls[mediaId]);

    candidates.forEach((mediaId) => {
      void fetchImageBlobUrlById(mediaId);
    });
  };

  const handleNext = () => {
    const nextStep = Math.min(boundedActiveStep + 1, Math.max(maxSteps - 1, 0));
    setIsCarouselImageResolving(false);
    setActiveStep(nextStep);
    if (hasTriggeredImageBlobFallbackRef.current) {
      warmupNearbyImageBlobs(nextStep);
    }
  };

  const handleBack = () => {
    const prevStep = Math.max(boundedActiveStep - 1, 0);
    setIsCarouselImageResolving(false);
    setActiveStep(prevStep);
    if (hasTriggeredImageBlobFallbackRef.current) {
      warmupNearbyImageBlobs(prevStep);
    }
  };

  const handleCarouselTouchStart = (event) => {
    if (isDesktopView || maxSteps <= 1) return;
    touchStartXRef.current = event.touches?.[0]?.clientX ?? null;
  };

  const handleCarouselTouchEnd = (event) => {
    if (isDesktopView || maxSteps <= 1) return;

    const startX = touchStartXRef.current;
    const endX = event.changedTouches?.[0]?.clientX ?? null;
    touchStartXRef.current = null;

    if (startX === null || endX === null) return;

    const deltaX = endX - startX;
    const SWIPE_THRESHOLD = 40;

    if (Math.abs(deltaX) < SWIPE_THRESHOLD) return;

    if (deltaX < 0) {
      handleNext();
      return;
    }

    handleBack();
  };

  const handleCarouselImageError = async (event) => {
    const imgEl = event.currentTarget;
    if (!imgEl) return;

    const activeSlide = slides[boundedActiveStep];
    const slideKey = `${activeSlide?.url || ""}|${activeSlide?.mediaId || ""}|${boundedActiveStep}`;

    if (imgEl.dataset.fallbackAttemptedFor === slideKey) {
      setIsCarouselImageResolving(false);
      handleImageError(event);
      return;
    }

    imgEl.dataset.fallbackAttemptedFor = slideKey;

    const fileId = activeSlide?.mediaId;
    if (!fileId) {
      setIsCarouselImageResolving(false);
      handleImageError(event);
      return;
    }

    hasTriggeredImageBlobFallbackRef.current = true;
    setIsCarouselImageResolving(true);

    try {
      const objectUrl = await fetchImageBlobUrlById(fileId);
      if (objectUrl) {
        imgEl.src = objectUrl;
        setIsCarouselImageResolving(false);
        warmupNearbyImageBlobs(boundedActiveStep);
        return;
      }
    } catch (error) {
      console.error("Detail image fallback fetch failed", { fileId, error });
    }

    setIsCarouselImageResolving(false);
    handleImageError(event);
  };

  const handleCarouselVideoError = async (event) => {
    const videoEl = event.currentTarget;
    if (!videoEl) return;

    const activeSlide = slides[boundedActiveStep];
    const slideKey = `${activeSlide?.url || ""}|${activeSlide?.mediaId || ""}|${boundedActiveStep}`;

    if (videoEl.dataset.fallbackAttemptedFor === slideKey) {
      return;
    }

    videoEl.dataset.fallbackAttemptedFor = slideKey;

    console.error("Video playback failed", {
      src: videoEl.currentSrc || videoEl.src,
      readyState: videoEl.readyState,
      networkState: videoEl.networkState,
      errorCode: videoEl.error?.code,
      errorMessage: videoEl.error?.message || "",
    });
  };

  const formatPrice = (price) => {
    if (!price || price === 0) {
      return "";
    }
    if (price >= 10000000) {
      return `₹${(price / 10000000).toFixed(1)}Cr`;
    } else if (price >= 100000) {
      return `₹${(price / 100000).toFixed(1)}L`;
    }
    return `₹${price.toLocaleString()}`;
  };

  const normalizedSubType = String(
    getProp("propertySubType", "property_sub_type", "")
  )
    .trim()
    .toLowerCase();
  const isVillaOrComplex =
    normalizedSubType === "villa" || normalizedSubType === "complex";
  const isLandOrPlot =
    normalizedSubType === "land" || normalizedSubType === "plot";
  const shouldShowLandSection = isLandOrPlot || isVillaOrComplex;
  const shouldShowBuildingSection = !isLandOrPlot || isVillaOrComplex;

  const renderEmptyValue = (value) => {
    if (value === null || value === undefined) return "";
    return value;
  };

  const formatArrayDisplay = (value) => {
    if (value === null || value === undefined) return "";

    const toDisplayString = (items) =>
      items
        .filter((item) => item !== null && item !== undefined && String(item).trim() !== "")
        .map((item) => String(item).trim())
        .join(", ");

    if (Array.isArray(value)) {
      return toDisplayString(value);
    }

    if (typeof value === "string") {
      const parsed = parseArray(value);
      return parsed.length > 0 ? toDisplayString(parsed) : value;
    }

    const parsed = parseArray(value);
    if (parsed.length > 0) return toDisplayString(parsed);
    if (typeof value === "number" || typeof value === "boolean") return String(value);
    return "";
  };

  const hasDisplayValue = (value) => {
    if (value === null || value === undefined) return false;
    const normalized = String(value).trim().toLowerCase();
    if (!normalized) return false;
    const emptyTokens = new Set(["n/a", "na", "not available", "null", "undefined", "-"]);
    return !emptyTokens.has(normalized);
  };

  const propertyTitle = getProp("title", "property_type", "");
  
  // Use backend-provided share metadata, fallback to derived values if not available
  const sharePropertyTitle = shareMetadata?.title || propertyTitle || "Property";
  const sharePropertyDescription = shareMetadata?.shortDescription || "";
  const sharePropertyImageUrl = shareMetadata?.shareImageUrl || slides.find((slide) => slide.type === "image")?.url || "";
  // For in-modal display, prefer the already-loaded slide blob/URL so auth-gated images render correctly
  const shareDisplayImageUrl = slides.find((slide) => slide.type === "image")?.url || sharePropertyImageUrl || "";
  // Always share a canonical property details URL.
  // Reuse existing encrypted route id when present to match logged-in URL behavior exactly.
  const canonicalPath = buildCanonicalPropertyPath();
  const sharePropertyUrl = `${window.location.origin}${canonicalPath}${shareUrlNonce ? `?spv=${shareUrlNonce}` : ""}`;
  const shareBrandTitle = "🏠 Vruddhi Properties";
  const shareBrandTagline = "Your go to platform to manage your properties needs";
  
  // Image URL is intentionally excluded — WhatsApp/Telegram generate
  // the image preview card automatically via the OG tags on the shared URL.
  const shareContentText = [
    shareBrandTitle,
    shareBrandTagline,
    "",
    "",
    sharePropertyUrl,
  ]
    .filter(Boolean)
    .join("\n");
  
  const sharePayload = {
    text: shareContentText,
  };

  const expectedPriceValue = Number(getProp("expectedPrice", "expected_price", 0));
  const minPriceValue = Number(getProp("minPrice", "min_price", 0));
  const propertySubTypeLabel = getProp("propertySubType", "property_sub_type", "");
  const propertyIdLabel =
    property.property_id || property.propertyId || property.propertyCode || property.id;
  const propertyTypeLabel = property.propertyType || property.property_type || "";
  const isLikelyUrl = (value) =>
    typeof value === "string" && /^(https?:\/\/|www\.)/i.test(value.trim());
  const rawLandmark = property.landmark || "";
  const displayLandmark =
    typeof rawLandmark === "string" && rawLandmark.trim().toLowerCase() === "location not specified"
      ? ""
      : rawLandmark;
  const rawLocation = property.location || property.area || "";
  const displayLocation =
    typeof rawLocation === "string" &&
    (rawLocation.trim().toLowerCase() === "location not specified" || isLikelyUrl(rawLocation))
      ? ""
      : rawLocation;
  const displayDistrict = hasDisplayValue(property.district) ? String(property.district).trim() : "";
  const localityParts = [property.taluk, displayDistrict].filter(hasDisplayValue);
  const perCentPriceValue = getProp("perCentPrice", "per_cent_price", 0);
  const perSqftPriceValue = getProp("perSqftPrice", "per_sqft_price", 0);
  const perUnitPriceLabel =
    perCentPriceValue && Number(perCentPriceValue) > 0
      ? `${perCentPriceValue} Lakhs/Cent`
      : perSqftPriceValue && Number(perSqftPriceValue) > 0
      ? `₹${perSqftPriceValue}/sq ft`
      : "";
  const roadApproachValue = renderEmptyValue(formatArrayDisplay(property.roadApproach));
  const roadTypeValue = renderEmptyValue(formatArrayDisplay(property.roadType));
  const waterSourceValue = renderEmptyValue(formatArrayDisplay(
    getProp("waterSource", "water_source", "")
  ));
  const sewageTypeValue = renderEmptyValue(formatArrayDisplay(
    getProp("sewageType", "sewage_type", "")
  ));

  const formatAreaValue = (value, suffix) => {
    if (value === null || value === undefined || value === "") return "";
    const numericValue = Number(value);
    if (Number.isFinite(numericValue) && numericValue > 0) {
      return `${numericValue.toLocaleString()} ${suffix}`;
    }
    return "";
  };

  const getDisplayExtentUnit = (unitValue) => {
    const normalizedUnit = String(unitValue || "").trim().toLowerCase();
    if (!normalizedUnit || normalizedUnit === "cent" || normalizedUnit === "cents") {
      return "Cents";
    }
    if (normalizedUnit === "sq ft" || normalizedUnit === "sqft") {
      return "Sqft";
    }
    if (normalizedUnit === "acre" || normalizedUnit === "acres") {
      return "Acres";
    }
    return String(unitValue).trim();
  };

  const DocumentStatus = ({ available, label, documents }) => {
    // Show checkmark if documents exist OR if marked as available
    const hasDocuments = documents && documents.length > 0;
    const isAvailable = hasDocuments || available;

    const handleDocumentDownload = async (doc) => {
      try {
        // If doc has a direct URL, use it
        if (doc?.url && (doc.url.startsWith('http') || doc.url.startsWith('blob:'))) {
          const downloadLink = document.createElement('a');
          downloadLink.href = doc.url;
          downloadLink.download = doc.name || 'document';
          downloadLink.style.display = 'none';
          document.body.appendChild(downloadLink);
          downloadLink.click();
          document.body.removeChild(downloadLink);
          return;
        }

        // If doc has file_id, fetch the blob first
        const fileId = doc?.file_id || doc?.id || doc?.fileId || null;
        const filename = doc?.filename || doc?.name || doc?.originalName || null;

        if (fileId) {
          try {
            const response = await UseAxios(fetchFileById({ fileId }));
            let blob = null;

            if (response?.data instanceof Blob) {
              blob = response.data;
            } else if (response?.data instanceof ArrayBuffer) {
              blob = new Blob([response.data]);
            }

            if (blob && blob.size > 0) {
              const objectUrl = URL.createObjectURL(blob);
              const downloadLink = document.createElement('a');
              downloadLink.href = objectUrl;
              downloadLink.download = filename || `document-${fileId}`;
              downloadLink.style.display = 'none';
              document.body.appendChild(downloadLink);
              downloadLink.click();
              document.body.removeChild(downloadLink);
              setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
              return;
            }
          } catch (error) {
            console.warn('Fetch by fileId failed, trying filename fallback', { fileId, error });
          }
        }

        if (filename) {
          const blob = await fetchBlobByFileName(filename);
          if (blob && blob.size > 0) {
            const objectUrl = URL.createObjectURL(blob);
            const downloadLink = document.createElement('a');
            downloadLink.href = objectUrl;
            downloadLink.download = filename;
            downloadLink.style.display = 'none';
            document.body.appendChild(downloadLink);
            downloadLink.click();
            document.body.removeChild(downloadLink);
            setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
            return;
          }

          try {
            const response = await legalDocumentAPI.fetchDocument(filename);
            let blob = null;
            if (response?.data instanceof Blob) blob = response.data;
            else if (response?.data instanceof ArrayBuffer) blob = new Blob([response.data]);
            if (blob && blob.size > 0) {
              const objectUrl = URL.createObjectURL(blob);
              const downloadLink = document.createElement('a');
              downloadLink.href = objectUrl;
              downloadLink.download = filename;
              downloadLink.style.display = 'none';
              document.body.appendChild(downloadLink);
              downloadLink.click();
              document.body.removeChild(downloadLink);
              setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
              return;
            }
          } catch (error) {
            console.warn('legalDocumentAPI.fetchDocument failed', { filename, error });
          }
        }

        throw new Error('No file ID or filename found for document');
      } catch (error) {
        console.error('Download failed:', error);
        alert(`Failed to download document: ${error.message}`);
      }
    };
    
    return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        p: 1.5,
        bgcolor: "grey.50",
        borderRadius: 1,
        gap: 1,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="body2" fontWeight={500}>{label}</Typography>
        {isAvailable ? (
          <CheckCircleIcon sx={{ fontSize: 16, color: "success.main" }} />
        ) : (
          <XCircleIcon sx={{ fontSize: 16, color: "error.main" }} />
        )}
      </Box>
      {hasDocuments && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mt: 0.5 }}>
          {documents.map((doc, index) => (
            <Box
              key={index}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                p: 0.75,
                bgcolor: "background.paper",
                borderRadius: 0.5,
                border: "1px solid",
                borderColor: "grey.200",
              }}
            >
              <FileIcon sx={{ fontSize: 16, color: "primary.main" }} />
              <Typography
                variant="caption"
                sx={{
                  flex: 1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {doc.filename || doc.name || `Document ${index + 1}`}
              </Typography>
              <IconButton
                size="small"
                onClick={() => handleDocumentDownload(doc)}
                sx={{ p: 0.5 }}
              >
                <DownloadIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}
    </Box>
    );
  };

  const pageTextScaleSx = {
    "& .MuiTypography-h4": { fontSize: { xs: "2.125rem", md: "1.85rem" } },
    "& .MuiTypography-h6": { fontSize: { xs: "1.25rem", md: "1.2rem" } },
    "& .MuiTypography-subtitle2": { fontSize: { xs: "0.875rem", md: "1rem" } },
    "& .MuiTypography-body1": { fontSize: { xs: "1rem", md: "1.05rem" } },
    "& .MuiTypography-body2": { fontSize: { xs: "0.875rem", md: "1.1rem" } },
    "& .MuiTypography-caption": { fontSize: { xs: "0.75rem", md: "0.85rem" } },
    "& .MuiButton-root": { fontSize: { xs: "0.875rem", md: "0.95rem" } },
    "& .MuiChip-label": { fontSize: { xs: "0.875rem", md: "0.95rem" } },
    "& .MuiInputBase-input": { fontSize: { xs: "1rem", md: "0.98rem" } },
  };

  const isPropertyOwner = isLoggedIn && userProfile && (parseInt(property.user_id) === parseInt(userProfile.id));
  const canViewLegalDocuments = isPropertyOwner || isAdmin;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "background.default",
        ...pageTextScaleSx,
      }}
    >
      <Header
        isLoggedIn={isLoggedIn}
        isAdmin={isAdmin}
        userEmail={userProfile?.email || ""}
        onSignIn={() => navigate("/login")}
        onSignOut={() => {
          logout();
          navigate("/");
        }}
        onHomeClick={() => navigate("/")}
        onMyPropertiesClick={() => navigate("/my-properties")}
        onManageMyPropertiesClick={() => navigate("/property-list")}
        onUnderVerificationClick={() => navigate('/all-properties')}
        onUsersClick={() => navigate('/users')}
        onMyProfileClick={() => navigate("/my-profile")}
        onSavedPropertiesClick={() => navigate("/saved-properties")}
        onSearch={handleSearch}
      />

      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ mb: 3 }}>
          {/* Image Carousel */}
          <Box sx={{ position: "relative", mb: 3 }}>
            <Box
              sx={{
                width: "100%",
                minHeight: { xs: 250, md: 340 },
                maxHeight: { xs: 420, md: 580 },
                bgcolor: "grey.900",
                borderRadius: 2,
                overflow: "hidden",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              onTouchStart={handleCarouselTouchStart}
              onTouchEnd={handleCarouselTouchEnd}
            >
              {!hasMedia ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "100%",
                    minHeight: { xs: 300, md: 400 },
                    backgroundColor: "grey.100",
                    color: "text.secondary",
                    fontWeight: 500,
                    fontSize: "1rem",
                  }}
                >
                  No image available
                </Box>
              ) : slides[boundedActiveStep]?.type === "video" ? (
                <Box
                  component="video"
                  key={`${slides[boundedActiveStep]?.url || "video"}-${slides[boundedActiveStep]?.mediaId || ""}`}
                  src={slides[boundedActiveStep]?.url}
                  controls
                  autoPlay
                  muted
                  playsInline
                  preload="auto"
                  onError={handleCarouselVideoError}
                  sx={{
                    width: "100%",
                    height: "100%",
                    maxHeight: { xs: 420, md: 580 },
                    borderRadius: 2,
                    objectFit: "contain",
                    backgroundColor: "black",
                  }}
                >
                  Your browser does not support the video tag.
                </Box>
              ) : isCarouselImageResolving ? (
                <Box
                  sx={{
                    width: "100%",
                    height: "100%",
                    minHeight: { xs: 250, md: 340 },
                    maxHeight: { xs: 420, md: 580 },
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "grey.100",
                    color: "text.secondary",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1,
                      color: "#6b7280",
                      fontSize: 12,
                    }}
                  >
                    <CircularProgress size={28} thickness={4} sx={{ color: "#1976d2" }} />
                    <Typography sx={{ fontSize: 12, color: "#9ca3af" }}>
                      Loading image...
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <CardMedia
                  key={`${slides[boundedActiveStep]?.url || "slide"}-${slides[boundedActiveStep]?.mediaId || ""}-${boundedActiveStep}`}
                  component="img"
                  image={slides[boundedActiveStep]?.url}
                  alt={buildPropertyImageAlt(property, {
                    context: "gallery image",
                    index: boundedActiveStep,
                    total: slides.length,
                  })}
                  referrerPolicy="no-referrer"
                  loading="eager"
                  fetchPriority="high"
                  decoding="async"
                  sx={{
                    width: "100%",
                    height: "100%",
                    maxHeight: { xs: 420, md: 580 },
                    borderRadius: 2,
                    objectFit: "contain",
                  }}
                  onLoad={(event) => {
                    setIsCarouselImageResolving(false);
                    delete event.currentTarget.dataset.fallbackAttemptedFor;
                  }}
                  onError={handleCarouselImageError}
                />
              )}
            </Box>

            {maxSteps > 1 && isDesktopView && (
              <>
                <IconButton
                  size="medium"
                  onClick={handleBack}
                  disabled={boundedActiveStep === 0}
                  sx={{
                    position: "absolute",
                    left: 12,
                    top: "50%",
                    transform: "translateY(-50%)",
                    bgcolor: "rgba(255,255,255,0.85)",
                    color: "text.primary",
                    zIndex: 2,
                    "&:hover": {
                      bgcolor: "white",
                    },
                    "&.Mui-disabled": {
                      bgcolor: "rgba(255,255,255,0.45)",
                    },
                  }}
                  aria-label="Previous image"
                >
                  <KeyboardArrowLeft />
                </IconButton>
                <IconButton
                  size="medium"
                  onClick={handleNext}
                  disabled={boundedActiveStep === maxSteps - 1}
                  sx={{
                    position: "absolute",
                    right: 12,
                    top: "50%",
                    transform: "translateY(-50%)",
                    bgcolor: "rgba(255,255,255,0.85)",
                    color: "text.primary",
                    zIndex: 2,
                    "&:hover": {
                      bgcolor: "white",
                    },
                    "&.Mui-disabled": {
                      bgcolor: "rgba(255,255,255,0.45)",
                    },
                  }}
                  aria-label="Next image"
                >
                  <KeyboardArrowRight />
                </IconButton>
              </>
            )}

            {preloadImageSlides.length > 0 && (
              <Box sx={{ display: "none" }} aria-hidden>
                {preloadImageSlides.map((slide) => (
                  <img
                    key={`preload-${slide.url}`}
                    src={slide.url}
                    alt=""
                    loading="eager"
                    decoding="async"
                    referrerPolicy="no-referrer"
                  />
                ))}
              </Box>
            )}
            <Box
              sx={{
                position: "absolute",
                top: 12,
                left: 12,
                display: "flex",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              <Chip
                label={getProp(
                  "currentAvailability",
                  "current_availability",
                  "Available"
                )}
                size="small"
                color={
                  getProp("currentAvailability", "current_availability") ===
                  "Available"
                    ? "success"
                    : getProp("currentAvailability", "current_availability") ===
                      "Under Negotiation"
                    ? "warning"
                    : "default"
                }
                sx={{ color: "white", fontWeight: "bold" }}
              />
              {getProp("featured", "featured") && (
                <Chip
                  label="Featured"
                  size="small"
                  sx={{ bgcolor: "orange", color: "white" }}
                />
              )}
              {hasDisplayValue(propertySubTypeLabel) && (
                <Chip
                  label={propertySubTypeLabel}
                  size="small"
                  color="primary"
                  sx={{ color: "white" }}
                />
              )}
            </Box>
            <Box
              sx={{
                position: "absolute",
                top: 12,
                right: 12,
                display: "flex",
                gap: 1,
              }}
            >
              <IconButton
                size="small"
                onClick={handleToggleSave}
                disabled={isSaving}
                sx={{
                  bgcolor: "rgba(255,255,255,0.9)",
                  color: isSaved ? "error.main" : "text.secondary",
                  "&:hover": {
                    bgcolor: "white",
                  },
                }}
              >
                {isSaving ? (
                  <CircularProgress size={20} />
                ) : isSaved ? (
                  <HeartIcon />
                ) : (
                  <HeartOutlineIcon />
                )}
              </IconButton>
              <IconButton
                size="small"
                onClick={handleShareClick}
                sx={{
                  bgcolor: "rgba(255,255,255,0.9)",
                  "&:hover": {
                    bgcolor: "white",
                  },
                }}
              >
                <ShareIcon />
              </IconButton>
            </Box>

            {/* Image counter */}
            {maxSteps > 1 && (
              <Box
                sx={{
                  position: "absolute",
                  bottom: 12,
                  right: 12,
                  bgcolor: "rgba(0, 0, 0, 0.7)",
                  color: "white",
                  px: 2,
                  py: 0.5,
                  borderRadius: 2,
                }}
              >
                <Typography variant="caption">
                  {boundedActiveStep + 1} / {maxSteps}
                </Typography>
              </Box>
            )}
          </Box>

          <Grid container spacing={3}>
            {/* Property Details */}
            <Grid item xs={12} lg={(() => {
              const isPropertyOwner = isLoggedIn && userProfile && (parseInt(property.user_id) === parseInt(userProfile.id));
              return isPropertyOwner ? 12 : 8;
            })()}>
              <Box sx={{ mb: 3 }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    alignItems: { xs: "flex-start", sm: "center" },
                    justifyContent: "space-between",
                    mb: 2,
                  }}
                >
                  {hasDisplayValue(propertyTitle) && (
                    <Typography
                      variant="h6"
                      component="h2"
                      sx={{
                        fontWeight: 600,
                        mb: { xs: 2, sm: 0 },
                        textAlign: "left",
                        pl: 0,
                        width: "100%",
                      }}
                    >
                      {propertyTitle}
                    </Typography>
                  )}
                  <Box sx={{ textAlign: { xs: "left", sm: "right" } }}>
                    {expectedPriceValue > 0 && (
                      <Typography
                        variant="h4"
                        sx={{ fontWeight: 700, color: "primary.main" }}
                      >
                        {formatPrice(expectedPriceValue)}
                      </Typography>
                    )}
                    {hasDisplayValue(perUnitPriceLabel) && (
                      <Typography variant="body2" color="text.secondary">
                        {perUnitPriceLabel}
                      </Typography>
                    )}
                    {minPriceValue > 0 && (
                      <Typography variant="body2" color="text.secondary">
                        Min: {formatPrice(minPriceValue)}
                      </Typography>
                    )}
                  </Box>
                </Box>

                {(displayLandmark || displayLocation || localityParts.length > 0) && (
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "20px 1fr",
                      columnGap: 0.5,
                      alignItems: displayLandmark || displayLocation ? "start" : "center",
                      mb: 2,
                    }}
                  >
                    <Box sx={{ display: "flex", flexShrink: 0 }}>
                      <MapPinIcon sx={{ fontSize: 16, mt: displayLandmark || displayLocation ? 0.2 : 0 }} />
                    </Box>
                    <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0, alignItems: "flex-start", gap: 0 }}>
                      {displayLandmark && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            m: 0,
                            p: 0,
                            width: "100%",
                            textAlign: "left",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            lineHeight: 1.2,
                          }}
                        >
                          {displayLandmark}
                        </Typography>
                      )}
                      {displayLocation && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            m: 0,
                            p: 0,
                            width: "100%",
                            textAlign: "left",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            lineHeight: 1.2,
                          }}
                        >
                          {displayLocation}
                        </Typography>
                      )}
                      {localityParts.length > 0 && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            m: 0,
                            p: 0,
                            width: "100%",
                            textAlign: "left",
                            fontSize: "0.75rem",
                            lineHeight: 1.2,
                          }}
                        >
                          {localityParts.join(", ")}{displayDistrict ? ", Karnataka" : ""}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 3,
                    color: "text.secondary",
                    flexWrap: "wrap",
                  }}
                >
                  {shouldShowBuildingSection && (property.bedroom_count || property.bedrooms) && (property.bedroom_count || property.bedrooms) > 0 && (
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <BedIcon sx={{ fontSize: 16, mr: 0.5 }} />
                      <Typography variant="body2">
                        {property.bedroom_count || property.bedrooms} Bedrooms
                      </Typography>
                    </Box>
                  )}
                  {shouldShowBuildingSection && property.bathrooms && property.bathrooms > 0 && (
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <BathIcon sx={{ fontSize: 16, mr: 0.5 }} />
                      <Typography variant="body2">
                        {property.bathrooms} Bathrooms
                      </Typography>
                    </Box>
                  )}
                  {shouldShowBuildingSection && property.carpetArea && property.carpetArea > 0 && (
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <SquareIcon sx={{ fontSize: 16, mr: 0.5 }} />
                      <Typography variant="body2">
                        {property.carpetArea?.toLocaleString()} sq ft
                      </Typography>
                    </Box>
                  )}
                  {shouldShowBuildingSection && property.parking && property.parking > 0 && (
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <CarIcon sx={{ fontSize: 16, mr: 0.5 }} />
                      <Typography variant="body2">
                        {property.parking} Parking
                      </Typography>
                    </Box>
                  )}
                  {shouldShowBuildingSection && property.floors && property.floors > 0 && (
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <BuildingIcon sx={{ fontSize: 16, mr: 0.5 }} />
                      <Typography variant="body2">
                        {property.floors} Floors
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Property Information */}
              <Grid container rowSpacing={3} columnSpacing={7} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, mb: 2, textAlign: "left", pl: 0 }}
                  >
                    Property Details
                  </Typography>
                  <Stack spacing={1}>
                    {hasDisplayValue(propertyIdLabel) && (
                      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Typography variant="body2" color="text.secondary">
                          Property ID:
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {propertyIdLabel}
                        </Typography>
                      </Box>
                    )}
                    {hasDisplayValue(propertyTypeLabel) && (
                      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Typography variant="body2" color="text.secondary">
                          Property Type:
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {propertyTypeLabel}
                        </Typography>
                      </Box>
                    )}
                    {hasDisplayValue(propertySubTypeLabel) && (
                      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Typography variant="body2" color="text.secondary">
                          Property Sub Type:
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {propertySubTypeLabel}
                        </Typography>
                      </Box>
                    )}
                    {shouldShowBuildingSection &&
                      hasDisplayValue(formatAreaValue(property.carpetArea, "sq ft")) && (
                      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Typography variant="body2" color="text.secondary">
                          Carpet Area:
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {formatAreaValue(property.carpetArea, "sq ft")}
                        </Typography>
                      </Box>
                    )}
                    {shouldShowBuildingSection &&
                      hasDisplayValue(formatAreaValue(property.plinthArea, "sq ft")) && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          Plinth Area:
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {formatAreaValue(property.plinthArea, "sq ft")}
                        </Typography>
                      </Box>
                    )}
                    {shouldShowLandSection &&
                      hasDisplayValue(
                        formatAreaValue(
                          property.extentCents,
                          getDisplayExtentUnit(property.unit)
                        )
                      ) && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          Extent:
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {formatAreaValue(
                            property.extentCents,
                            getDisplayExtentUnit(property.unit)
                          )}
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </Grid>

                {shouldShowLandSection && (
                <Grid item xs={12} md={6}>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, mb: 2, textAlign: "left", pl: 0 }}
                  >
                    Infrastructure
                  </Typography>
                  <Stack spacing={1}>
                    {hasDisplayValue(roadApproachValue) && (
                      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Typography variant="body2" color="text.secondary">
                          Road Approach:
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {roadApproachValue}
                        </Typography>
                      </Box>
                    )}
                    {hasDisplayValue(roadTypeValue) && (
                      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Typography variant="body2" color="text.secondary">
                          Road Type:
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {roadTypeValue}
                        </Typography>
                      </Box>
                    )}
                    {Boolean(property.electricity) && (
                      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Typography variant="body2" color="text.secondary">
                          Electricity:
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          Available
                        </Typography>
                      </Box>
                    )}
                    {hasDisplayValue(waterSourceValue) && (
                      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Typography variant="body2" color="text.secondary">
                          Water Source:
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {waterSourceValue}
                        </Typography>
                      </Box>
                    )}
                    {hasDisplayValue(sewageTypeValue) && (
                      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Typography variant="body2" color="text.secondary">
                          Sewage Type:
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {sewageTypeValue}
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </Grid>
                )}
              </Grid>

              <Divider sx={{ my: 3 }} />

              {/* Additional Details */}
              {(property.otherDetails || property.anyOtherDetails) && (
                <>
                  <Box sx={{ mb: 3, textAlign: "left" }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                      Additional Information
                    </Typography>
                    <Box sx={{ color: "text.secondary", textAlign: "left" }}>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        {property.otherDetails}
                      </Typography>
                      <Typography variant="body2">
                        {property.anyOtherDetails}
                      </Typography>
                    </Box>
                  </Box>
                  <Divider sx={{ my: 3 }} />
                </>
              )}

              {/* Legal Documents - Show for property owner or admin */}
              {canViewLegalDocuments && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    Legal Documents
                  </Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={12} sm={6}>
                      <DocumentStatus
                        available={property.rtcAvailable}
                        label="RTC"
                        documents={getDisplayDocuments("rtc", property.legalDocuments?.rtc)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <DocumentStatus
                        available={property.khataAvailable}
                        label="Khata"
                        documents={getDisplayDocuments("khata", property.legalDocuments?.khata)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <DocumentStatus
                        available={property.saleDeedAvailable}
                        label="Sale Deed"
                        documents={getDisplayDocuments("sale_deed", property.legalDocuments?.sale_deed)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <DocumentStatus
                        available={property.fmbNaksheAvailable}
                        label="FMB/Nakshe"
                        documents={getDisplayDocuments("fmb_nakshe", property.legalDocuments?.fmb_nakshe)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <DocumentStatus
                        available={property.ecAvailable}
                        label="EC"
                        documents={getDisplayDocuments("ec", property.legalDocuments?.ec)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <DocumentStatus
                        available={property.mutationAvailable}
                        label="Mutation"
                        documents={getDisplayDocuments("mutation", property.legalDocuments?.mutation)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <DocumentStatus
                        available={property.motherDeedAvailable}
                        label="Mother Deed"
                        documents={getDisplayDocuments("mother_deed", property.legalDocuments?.mother_deed)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <DocumentStatus
                        available={property.documentWithVruddhi}
                        label="Document with Vruddhi"
                      />
                    </Grid>
                  </Grid>
                  <Divider sx={{ my: 3 }} />
                </Box>
              )}
            </Grid>

            {/* Contact Information - Only show if user is not the property owner */}
            {(() => {
              const getFirstValue = (...values) => {
                for (const value of values) {
                  if (value !== null && value !== undefined && String(value).trim() !== "") {
                    return value;
                  }
                }
                return "";
              };

              const sellerType = String(property.sellingAs || property.selling_as || "Owner").toLowerCase();
              const isBrokerSeller = sellerType === "broker";
              // const sellerName = isBrokerSeller
              //   ? getFirstValue(property.broker, property.broker_name)
              //   : getFirstValue(property.ownerName, property.owner_name);
              // const sellerPhone = isBrokerSeller
              //   ? getFirstValue(property.brokerContact, property.broker_contact, property.broker_phone_number)
              //   : getFirstValue(property.ownerContact, property.owner_contact, property.owner_phone_number);
              const sellerName = "Prakash";
              const sellerPhone = "6360732460";
              const sellerEmail = isBrokerSeller
                ? getFirstValue(property.brokerEmail, property.broker_email_id, property.broker_email)
                : getFirstValue(property.ownerEmail, property.owner_email_id, property.owner_email);
              const sellerInitial = String(sellerName || "")
                .trim()
                .charAt(0)
                .toUpperCase();
              const hasSellerContact = !!(sellerName || sellerPhone || sellerEmail);
              const canViewContactDetails = !!isLoggedIn;

              const isPropertyOwner = isLoggedIn && userProfile && (parseInt(property.user_id) === parseInt(userProfile.id));

              if (isPropertyOwner) {
                return null;
              }

              return (
                <Grid item xs={12} lg={4}>
                  <Card sx={{ position: "sticky", top: 24 }}>
                    <CardContent sx={{ p: 3 }}>
                      {!canViewContactDetails ? (
                        <Box sx={{ textAlign: "center" }}>
                          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                            Contact Details
                          </Typography>
                          <Button
                            variant="contained"
                            fullWidth
                            onClick={() => navigate("/login")}
                          >
                            Sign in to view contact details
                          </Button>
                        </Box>
                      ) : (
                        <>
                          <Box sx={{ textAlign: "center", mb: 3 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                              Contact Details
                            </Typography>
                            <Avatar
                              sx={{
                                width: 64,
                                height: 64,
                                mx: "auto",
                                mb: 1,
                                bgcolor: "grey.300",
                              }}
                            >
                              <Typography variant="h5" fontWeight="bold">
                                {sellerInitial || (isBrokerSeller ? "B" : "O")}
                              </Typography>
                            </Avatar>
                            {hasSellerContact ? (
                              <Stack spacing={0.75} sx={{ mt: 1 }}>
                                {sellerName && (
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {sellerName}
                                  </Typography>
                                )}
                                {sellerEmail && (
                                  <Typography
                                    component="a"
                                    href={`mailto:${sellerEmail}`}
                                    variant="body2"
                                    sx={{
                                      color: "primary.main",
                                      textDecoration: "none",
                                      "&:hover": {
                                        textDecoration: "underline",
                                      },
                                    }}
                                  >
                                    {sellerEmail}
                                  </Typography>
                                )}
                                {sellerPhone && (
                                  <Typography
                                    component="a"
                                    href={`tel:+91${sellerPhone}`}
                                    variant="body2"
                                    sx={{
                                      color: "primary.main",
                                      textDecoration: "none",
                                      "&:hover": {
                                        textDecoration: "underline",
                                      },
                                    }}
                                  >
                                    {`+91 ${sellerPhone}`}
                                  </Typography>
                                )}
                              </Stack>
                            ) : (
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                No contact info available
                              </Typography>
                            )}
                            {isBrokerSeller && (
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                                Licensed Real Estate Broker
                              </Typography>
                            )}
                            {/* <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                              Direct Owner Sale
                            </Typography> */}
                          </Box>

                          {property.caretaker && (
                            <>
                              <Divider sx={{ my: 2 }} />
                              <Box sx={{ textAlign: "center" }}>
                                <Typography
                                  variant="subtitle2"
                                  sx={{ fontWeight: 600 }}
                                >
                                  Caretaker
                                </Typography>
                                <Stack spacing={0.5} sx={{ mt: 1 }}>
                                  <Typography variant="body2">
                                    {property.caretaker}
                                  </Typography>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      gap: 1,
                                    }}
                                  >
                                    <PhoneIcon sx={{ fontSize: 12 }} />
                                    <Typography
                                      component="a"
                                      href={`tel:+91${property.caretakerContact}`}
                                      variant="body2"
                                      sx={{
                                        color: "primary.main",
                                        textDecoration: "none",
                                        "&:hover": {
                                          textDecoration: "underline",
                                        },
                                      }}
                                    >
                                      {`+91 ${property.caretakerContact}`}
                                    </Typography>
                                  </Box>
                                  {property.caretakerEmail && (
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        gap: 1,
                                      }}
                                    >
                                      <MailIcon sx={{ fontSize: 12 }} />
                                      <Typography
                                        component="a"
                                        href={`mailto:${property.caretakerEmail}`}
                                        variant="body2"
                                        sx={{
                                          color: "primary.main",
                                          textDecoration: "none",
                                          "&:hover": {
                                            textDecoration: "underline",
                                          },
                                        }}
                                      >
                                        {property.caretakerEmail}
                                      </Typography>
                                    </Box>
                                  )}
                                </Stack>
                              </Box>
                            </>
                          )}

                          <Divider sx={{ my: 2 }} />

                          <Stack spacing={2}>
                        {/* <Box>
                          <TextField
                            fullWidth
                            multiline
                            rows={4}
                            placeholder="I'm interested in this property. Please contact me with more information."
                          />
                        </Box>

                        <Button
                          variant="contained"
                          fullWidth
                          startIcon={<MailIcon />}
                        >
                          Send Message
                        </Button> */}
                            {sellerPhone && (
                              <Button
                                variant="outlined"
                                fullWidth
                                startIcon={<PhoneIcon />}
                                component="a"
                                href={`tel:+91${sellerPhone}`}
                              >
                                Call Now
                              </Button>
                            )}
                          </Stack>
                        </>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              );
            })()}
          </Grid>
        </Box>
      </Container>

      {/* Share Modal */}
      <Modal
        open={shareModalOpen}
        onClose={handleCloseShareModal}
        aria-labelledby="share-modal-title"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "92%", sm: 420 },
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            overflow: "hidden",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              px: 2.5,
              pt: 2,
              pb: 1.5,
            }}
          >
            <Typography id="share-modal-title" variant="h6" fontWeight={600}>
              Share Property
            </Typography>
            <IconButton size="small" onClick={handleCloseShareModal}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Brand block */}
          <Box sx={{ px: 2.5, pb: 1.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
              <Box
                component="img"
                src={darpanIcon}
                alt="Vruddhi"
                sx={{ width: 20, height: 20, flexShrink: 0 }}
              />
              <Typography
                variant="subtitle2"
                fontWeight={700}
                sx={{ color: "#005487", letterSpacing: 0.2, fontSize: "0.82rem", lineHeight: 1.2 }}
              >
                Vruddhi Properties
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.72rem", lineHeight: 1.3 }}>
              Your go to platform to manage your properties needs
            </Typography>
          </Box>

          {/* Link + Copy row */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "stretch",
              gap: 0.75,
              mx: 2.5,
              mb: 2,
              px: 1.5,
              py: 1,
              bgcolor: "grey.100",
              borderRadius: 1,
            }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                display: "block",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                fontSize: "0.72rem",
              }}
            >
              {sharePropertyUrl}
            </Typography>
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                size="small"
                startIcon={<CopyIcon />}
                onClick={handleCopyLink}
                sx={{ minWidth: "auto", px: 1.5, flexShrink: 0, fontSize: "0.72rem" }}
              >
                Copy Link
              </Button>
            </Box>
          </Box>

          {/* Property card preview */}
          <Box sx={{ mx: 2.5, mb: 2, borderRadius: 1.5, overflow: "hidden", border: "1px solid", borderColor: "grey.200" }}>
            {shareDisplayImageUrl ? (
              <CardMedia
                component="img"
                image={shareDisplayImageUrl}
                alt={sharePropertyTitle}
                sx={{ width: "100%", height: 180, objectFit: "cover", display: "block" }}
                onError={handleImageError}
              />
            ) : (
              <Box
                sx={{
                  height: 180,
                  bgcolor: "grey.100",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography variant="body2" color="text.disabled">
                  No image available
                </Typography>
              </Box>
            )}
            <Box sx={{ px: 1.5, py: 1.25 }}>
              <Typography variant="subtitle1" fontWeight={700} noWrap>
                {sharePropertyTitle}
              </Typography>
              {(displayLandmark || displayLocation || localityParts.length > 0) && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.25 }}>
                  <MapPinIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {[displayLandmark || displayLocation, ...localityParts]
                      .filter(Boolean)
                      .join(", ")}
                  </Typography>
                </Box>
              )}
              {(propertySubTypeLabel || propertyTypeLabel) && (
                <Chip
                  label={propertySubTypeLabel || propertyTypeLabel}
                  size="small"
                  sx={{ mt: 0.75, height: 20, fontSize: "0.65rem" }}
                />
              )}
            </Box>
          </Box>

          {/* Share via apps button */}
          {navigator?.share ? (
            <Box sx={{ px: 2.5, pb: 2 }}>
              <Button variant="outlined" fullWidth onClick={handleNativeShare}>
                Share via Apps
              </Button>
            </Box>
          ) : null}

          {linkCopied && (
            <Alert severity="success" sx={{ mx: 2.5, mb: 2 }}>
              Link copied to clipboard!
            </Alert>
          )}
        </Box>
      </Modal>
    </Box>
  );
}
